import { CYLINDER_DETAIL } from '@locales/id/cylinders';
import { EVENT_LABEL, shipmentNumber } from '@locales/id/labels';
import type { CylinderEvent, CylinderShipment, JourneyStep } from '@lib/api';

const J = CYLINDER_DETAIL.journey;
const R = CYLINDER_DETAIL.reads;
const S = CYLINDER_DETAIL.shipments;

/** One step of the journey as a line: where it went (or was registered), from where, on which Surat Jalan, when. */
export interface JourneyLine { key: string; title: string; from: string | null; toId: number | null; shipment: { id: number; number: string } | null; at: string; registered: boolean }

export const journeyLine = (s: JourneyStep): JourneyLine => ({
  key: s.kind === 'registered' ? 'registered' : `m${s.id}`,
  title: s.kind === 'registered' ? J.registered(s.toName ?? J.elsewhere) : J.received(s.toName ?? J.elsewhere),
  from: s.kind === 'movement' && s.fromName ? J.from(s.fromName) : null,
  toId: s.toLocationId,
  shipment: s.shipmentId != null ? { id: s.shipmentId, number: shipmentNumber(s.shipmentNumber) } : null,
  at: s.at,
  registered: s.kind === 'registered',
});

/** One Reader read: what happened, by which Reader where, in what context (loading, receiving, a sighting). */
export interface ReadLine { key: string; what: string; reader: string; context: string; at: string; dwell: number | null }

/** Dwell in whole seconds, at least 1 (TAG_GONE carries how long the tag stayed in range, in the gateway's keys). */
const dwellOf = (e: CylinderEvent) => {
  const ms = e.data?.dwell_ms;
  return e.type === 'TAG_GONE' && typeof ms === 'number' ? Math.max(1, Math.round(ms / 1000)) : null;
};

export const readLine = (e: CylinderEvent): ReadLine => ({
  key: e.id,
  what: EVENT_LABEL[e.type] ?? e.type,
  // a Reader is often named after its place: say the place once then
  reader: (e.gwLabel || e.gwId) === e.locationName ? e.locationName : [e.gwLabel || e.gwId, e.locationName ?? R.unplaced].join(' · '),
  context: e.context === 'load' ? R.load(shipmentNumber(e.shipmentNumber)) : e.context === 'unload' ? R.unload(shipmentNumber(e.shipmentNumber)) : R.sighting,
  at: e.ts,
  dwell: dwellOf(e),
});

/** A Surat Jalan this cylinder travelled on: its number, the route, and how it ended for this cylinder. */
export const shipmentRoute = (s: CylinderShipment) => S.route(s.originName, s.destinationName);
